class MachineSelector extends HTMLElement {
  connectedCallback() {
    this.controller = window.getMachineController?.();

    if (!this.controller) return;

    this.controller.setFilterAttributes(
      this.dataset.machineFilterBrand || '',
      this.dataset.machineFilterModel || '',
      ''
    );

    this.brandSelector = this.querySelector('[data-brand-select]');
    this.brandSelectorVisual = this.querySelector('[data-brand-selector]');
    this.modelSelector = this.querySelector('[data-model-select]');
    this.modelSelectorVisual = this.querySelector('[data-model-selector]');
    this.generateButton = this.querySelector('[data-generate-link]');
    this.clearAllFiltersButton = this.querySelector('[data-clear-all-filters]');
    this.recentMachinesContainer = this.querySelector('[data-recent-machines-container]');

    this.originalButtonContent = this.generateButton.innerHTML;

    this.safeDisableSelector(this.modelSelectorVisual);

    this.brandSelector.addEventListener('change', (e) => this.changeBrand(e));
    this.modelSelector.addEventListener('change', (e) => this.changeModel(e));
    this.generateButton.addEventListener('click', () => this.handleGenerateButtonClick());
    this.clearAllFiltersButton.addEventListener('click', () => this.handleClearAllFilters());

    this.initializeRecentMachines();

    // Use setTimeout to ensure the component is fully rendered before loading state
    setTimeout(() => {
      this.loadStateFromController();
    }, 0);
  }

  disconnectedCallback() {
    if (this.unsubscribeRecentMachines) {
      this.unsubscribeRecentMachines();
    }
  }

  safeEnableSelector(selectorVisual) {
    if (!selectorVisual) return;
    const contentSelect = selectorVisual.closest('content-select');
    if (contentSelect && typeof contentSelect.enable === 'function') {
      contentSelect.enable();
    }
  }

  safeDisableSelector(selectorVisual) {
    if (!selectorVisual) return;
    const contentSelect = selectorVisual.closest('content-select');
    if (contentSelect && typeof contentSelect.disable === 'function') {
      contentSelect.disable();
    }
  }

  initializeRecentMachines() {
    const recentMachines = this.controller.getRecentMachines();
    this.updateRecentMachinesUI(recentMachines);
  }

  updateRecentMachinesUI(recentMachines) {
    if (!this.recentMachinesContainer) return;

    const currentState = this.controller.getCurrentState();

    const filteredRecentMachines = recentMachines.filter(
      (machine) => !(machine.brand === currentState.brand && machine.model === currentState.model)
    );

    if (filteredRecentMachines.length === 0) {
      this.recentMachinesContainer.classList.add('hidden');
      return;
    }

    this.recentMachinesContainer.classList.remove('hidden');
    const recentMachinesList = this.recentMachinesContainer.querySelector(
      '[data-recent-machines-list]'
    );

    if (!recentMachinesList) return;

    recentMachinesList.innerHTML = filteredRecentMachines
      .map(
        (machine) => `
      <button
        type="button"
        class="recent-machine-tag inline-flex items-center gap-1 cursor-pointer rounded-full bg-primary-100 px-2 py-0 text-2xs font-medium text-gray-700 transition-colors hover:bg-gray-200 hover:text-gray-900"
        data-brand="${machine.brand}"
        data-model="${machine.model}"
        data-brand-label="${machine.brandLabel}"
        data-model-label="${machine.modelLabel}"
        data-brand-gid="${machine.brandGid || ''}"
        data-model-gid="${machine.modelGid || ''}"
      >
        <span>${machine.brandLabel}</span>
        <span class="text-gray-400">/</span>
        <span>${machine.modelLabel}</span>
      </button>
    `
      )
      .join('');

    recentMachinesList.querySelectorAll('.recent-machine-tag').forEach((button) => {
      button.addEventListener('click', (e) => this.handleRecentMachineInstantRedirect(e));
    });
  }

  handleRecentMachineInstantRedirect(e) {
    const button = e.currentTarget;
    const machine = {
      brand: button.dataset.brand,
      model: button.dataset.model,
      brandLabel: button.dataset.brandLabel,
      modelLabel: button.dataset.modelLabel,
      brandGid: button.dataset.brandGid,
      modelGid: button.dataset.modelGid,
    };

    this.controller.setBrand(machine.brand, machine.brandGid);
    this.controller.setModel(machine.model, machine.modelGid);

    this.controller.addRecentMachine(
      machine.brand,
      machine.model,
      '',
      machine.brandLabel,
      machine.modelLabel,
      machine.brandGid,
      machine.modelGid
    );

    this.redirectToCollection();
  }

  handleRecentMachineSelect(e) {
    const button = e.currentTarget;
    const machine = {
      brand: button.dataset.brand,
      model: button.dataset.model,
      brandLabel: button.dataset.brandLabel,
      modelLabel: button.dataset.modelLabel,
      brandGid: button.dataset.brandGid,
      modelGid: button.dataset.modelGid,
    };

    this.controller.selectRecentMachine(machine);
    this.loadStateFromController();
  }

  resetSelector(selector, selectorVisual) {
    if (selector) selector.value = '';
    this.refreshContentSelect(selectorVisual, '');
  }

  enableSelector(selectorVisual) {
    this.safeEnableSelector(selectorVisual);
  }

  disableSelector(selectorVisual) {
    this.safeDisableSelector(selectorVisual);
  }

  filterOptions(selectorVisual, dataAttribute, value) {
    if (!selectorVisual) return;

    const allOptions = selectorVisual.querySelectorAll('[role="menuitem"]');

    allOptions.forEach((option) => {
      option.classList.add('hidden');
      option.setAttribute('disabled', 'true');
    });

    if (value) {
      const matchingOptions = selectorVisual.querySelectorAll(
        `[role="menuitem"][${dataAttribute}="${value}"]`
      );
      const unknownOptions = selectorVisual.querySelectorAll(
        '[role="menuitem"][data-unknown-option="true"]'
      );

      [...matchingOptions, ...unknownOptions].forEach((option) => {
        option.classList.remove('hidden');
        option.removeAttribute('disabled');
      });
    }
  }

  changeBrand(e) {
    const value = e.target.value;
    const gid = this.getSelectedBrandGid();
    this.controller.setBrand(value, gid);
    this._updateUIOnBrandChange(value);
    this.updateButtonState();
    this.refreshRecentMachinesDisplay();
  }

  _updateUIOnBrandChange(brandValue) {
    this.resetSelector(this.modelSelector, this.modelSelectorVisual);

    if (brandValue) {
      this.enableSelector(this.modelSelectorVisual);
      this.filterOptions(this.modelSelectorVisual, 'data-brand', brandValue);
    } else {
      this.disableSelector(this.modelSelectorVisual);
    }
    this.refreshContentSelect(this.modelSelectorVisual, '');
  }

  changeModel(e) {
    const value = e.target.value;
    const gid = this.getSelectedModelGid();
    this.controller.setModel(value, gid);
    this.updateButtonState();
    this.refreshRecentMachinesDisplay();
  }

  refreshRecentMachinesDisplay() {
    const recentMachines = this.controller.getRecentMachines();
    this.updateRecentMachinesUI(recentMachines);
  }

  handleGenerateButtonClick() {
    if (this.generateButton.textContent === 'Confirm') {
      this.redirectToCollection();
      return;
    }

    const brandValue = this.brandSelector.value;
    const modelValue = this.modelSelector.value;

    const brandLabel = this.getSelectedBrandLabel();
    const modelLabel = this.getSelectedModelLabel();
    const brandGid = this.getSelectedBrandGid();
    const modelGid = this.getSelectedModelGid();

    this.controller.setBrand(brandValue, brandGid);
    this.controller.setModel(modelValue, modelGid);

    if (brandValue && modelValue) {
      this.controller.addRecentMachine(
        brandValue,
        modelValue,
        '',
        brandLabel,
        modelLabel,
        brandGid,
        modelGid
      );
    }

    this.redirectToCollection();
  }

  redirectToCollection() {
    let listingUrl = this.getListingUrl();
    if (listingUrl.endsWith('/')) listingUrl = listingUrl.slice(0, -1);

    // Use the controller's metafield filtering method
    const filteredUrl = this.controller.getMetafieldFilterUrl(listingUrl);
    window.location.href = filteredUrl;
  }

  getSelectedBrandLabel() {
    if (!this.brandSelector) return '';
    const selectedOption = this.brandSelector.querySelector(
      `option[value="${this.brandSelector.value}"]`
    );
    return selectedOption ? selectedOption.textContent.trim() : this.brandSelector.value;
  }

  getSelectedModelLabel() {
    if (!this.modelSelector) return '';
    const selectedOption = this.modelSelector.querySelector(
      `option[value="${this.modelSelector.value}"]`
    );
    return selectedOption ? selectedOption.textContent.trim() : this.modelSelector.value;
  }

  getSelectedBrandGid() {
    if (!this.brandSelector) return '';
    const selectedOption = this.brandSelector.querySelector(
      `option[value="${this.brandSelector.value}"]`
    );
    return selectedOption ? selectedOption.dataset.gid || '' : '';
  }

  getSelectedModelGid() {
    if (!this.modelSelector) return '';
    const selectedOption = this.modelSelector.querySelector(
      `option[value="${this.modelSelector.value}"]`
    );
    return selectedOption ? selectedOption.dataset.gid || '' : '';
  }

  handleClearAllFilters() {
    this.controller.clearFilters();
    this.loadStateFromController();
    this.refreshRecentMachinesDisplay();
    this.clearCurrentPageFilters();
  }

  clearCurrentPageFilters() {
    if (this.isOnListingPage()) {
      const url = new URL(window.location);

      // Remove metafield filter parameters
      url.searchParams.delete('filter.p.m.custom.models');
      url.searchParams.delete('machine_model');

      window.location.href = url.toString();
    }
  }

  // eslint-disable-next-line complexity
  updateSelectedVisualInContentSelect(selectorVisual, valueToSet) {
    if (!selectorVisual) return;

    const contentSelect = selectorVisual.closest('content-select');
    if (!contentSelect || typeof contentSelect.initList !== 'function') return;

    contentSelect.initList();

    if (!valueToSet) {
      if (typeof contentSelect.selectOption === 'function') {
        contentSelect.selectOption(null);
      }
    } else {
      const optionToSelect = Array.from(contentSelect.options || []).find(
        (opt) => opt.dataset.value === valueToSet
      );
      if (typeof contentSelect.selectOption === 'function') {
        contentSelect.selectOption(optionToSelect || null);
      }
    }
  }

  refreshContentSelect(selectorVisual, currentValue) {
    if (!selectorVisual) return;

    const contentSelect = selectorVisual.closest('content-select');
    if (!contentSelect) return;

    contentSelect.dispatchEvent(new CustomEvent('content-select:refresh'));
    this.updateSelectedVisualInContentSelect(selectorVisual, currentValue);
  }

  updateButtonState() {
    if (!this.brandSelector || !this.modelSelector) return;

    const hasModel = this.modelSelector.value;

    if (hasModel) {
      this.generateButton.innerHTML = this.originalButtonContent;
      this.generateButton.disabled = false;
      this.generateButton.removeAttribute('disabled');
      this.clearAllFiltersButton.classList.remove('hidden');
    } else {
      this.generateButton.disabled = true;
      this.generateButton.toggleAttribute('disabled', true);
      this.clearAllFiltersButton.classList.add('hidden');
    }
  }

  // eslint-disable-next-line complexity
  loadStateFromController() {
    if (!this.controller) return;
    const currentState = this.controller.getCurrentState();

    if (this.brandSelector) {
      this.brandSelector.value = currentState.brand || '';
    }
    this.refreshContentSelect(this.brandSelectorVisual, currentState.brand || '');
    this._updateUIOnBrandChange(currentState.brand || '');

    if (currentState.brand && currentState.model && this.modelSelector) {
      this.modelSelector.value = currentState.model;
      this.refreshContentSelect(this.modelSelectorVisual, currentState.model);
    }

    this.updateButtonState();
  }

  getListingUrl() {
    if (this.isOnListingPage()) return this.getCurrentListingUrl();

    return this.generateButton.dataset.collectionUrl || '';
  }

  isOnListingPage() {
    return (
      window.location.pathname.includes('/collections/') ||
      window.location.pathname.includes('/products/')
    );
  }

  getCurrentListingUrl() {
    const listingMatch = window.location.pathname.match(/^(\/(?:[^/]+\/)?(?:collections|products)\/[^/]+)/);
    return listingMatch ? `${window.location.origin}${listingMatch[1]}` : '';
  }
}

customElements.define('machine-selector', MachineSelector);
